import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { FlexColumn, FormModal, SelectField, DateField, SmallBody, AttentionBox } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { required } from '@ubeya/shared/utils/form';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';

const Container = styled(FlexColumn)`
  gap: 16px;
`;

const FieldsContainer = styled(FlexColumn)`
  gap: 16px;
`;

const UseAllPtoBalanceModal = ({
  onClose,
  employeeUseAllPtoBalance,
  employeeBranches = [],
  employeeName,
  currentBalance,
  hasFutureJobs
}) => {
  const { t } = useContext(ConfigContext);
  const { addResponseError } = useToaster();
  const { branchesOptions } = useBranches();
  const formattingMap = useFormattingMap();

  const formattedBalance = useMemo(() => formattingMap.currency(currentBalance || 0, { includeZero: true }), [
    formattingMap,
    currentBalance
  ]);

  const branchPickerOptions = useMemo(() => {
    const employeeBranchesSet = new Set(employeeBranches);
    return branchesOptions.filter(({ value }) => employeeBranchesSet.has(value));
  }, [branchesOptions, employeeBranches]);

  const initialValues = useMemo(
    () => ({
      date: moment(),
      branchId: branchPickerOptions.length === 1 ? branchPickerOptions[0].value : undefined
    }),
    [branchPickerOptions]
  );

  const onSubmit = useCallback(
    async (values) => {
      const { branchId, date } = values;
      if (!branchId || !date) return;

      try {
        await employeeUseAllPtoBalance({
          branchId,
          date: moment(date).format(API_DATE_FORMAT)
        });
        trackEvent('Leave management - used all PTO balance', { module: 'Leave Management' });
      } catch (error) {
        addResponseError(error);
      } finally {
        onClose();
      }
    },
    [employeeUseAllPtoBalance, addResponseError, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('useAllPtoModalHeader')}
      confirmText={t('confirm')}
      showCancelButton
      initialValues={initialValues}
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <SmallBody>{t('useAllPtoModalBody', { currentBalance: formattedBalance, employeeName })}</SmallBody>

        {hasFutureJobs && <AttentionBox type="info">{t('futureJobsExistForThisEmployee')}</AttentionBox>}

        <FieldsContainer>
          <DateField name="date" title={t('date')} validate={required} required usePortal />

          <SelectField
            name="branchId"
            title={t('branch')}
            options={branchPickerOptions}
            validate={required}
            required
            usePortal
            isMulti={false}
          />
        </FieldsContainer>
      </Container>
    </FormModal>
  );
};

export default UseAllPtoBalanceModal;
